import { useEffect, useState } from 'react'

/**
 * The app font for the mail frame. The frame has an opaque origin (no
 * `allow-same-origin`), so it cannot fetch the app's font files without CORS
 * headers; it gets Figtree as a data URL instead. The font sits in its own lazy
 * chunk, loaded the first time a mail is shown, and is cached after that.
 */

let cached: string | null = null
let pending: Promise<string> | null = null

export function loadMailFontCss(): Promise<string> {
  pending ??= import('@fontsource-variable/figtree/files/figtree-latin-wght-normal.woff2?inline')
    .then(
      ({ default: url }) =>
        `@font-face{font-family:'Figtree Variable';font-style:normal;font-display:block;font-weight:300 900;src:url(${url}) format('woff2')}`,
    )
    // Without the font the frame falls back to the system sans; never block the mail on it.
    .catch(() => '')
    .then((css) => {
      cached = css
      return css
    })
  return pending
}

/** The `@font-face` rule for the frame, or null while it is still loading. */
export function useMailFontCss(): string | null {
  const [css, setCss] = useState<string | null>(cached)
  useEffect(() => {
    if (css !== null) return
    let live = true
    void loadMailFontCss().then((loaded) => {
      if (live) setCss(loaded)
    })
    return () => {
      live = false
    }
  }, [css])
  return css
}
